<template>
  <div
    :class="[
      'UIGradientSphere',
      { 'UIGradientSphere--is-configurable': isConfigurable },
    ]"
  >
    <UIGradientPickerDropdown
      v-model="editableGradient"
      @selected="emit('selected')"
    >
      <template #toggle="">
        <div
          :class="[
            'UIGradientSphere__sphere',
            `UIGradientSphere__sphere--${size}`,
            { 'UIGradientSphere__sphere--floating': isFloating },
          ]"
          :style="`background: ${editableGradient}`"
        />
      </template>
    </UIGradientPickerDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'

import UIGradientPickerDropdown from './UIGradientPickerDropdown.vue'

const props = withDefaults(
  defineProps<{
    gradient?: string
    size?: 'sm' | 'md' | 'xmd' | 'lg'
    isConfigurable?: boolean
    /**
     * If true then the sphere will have a floating effect
     */
    isFloating?: boolean
  }>(),
  {
    gradient:
      'linear-gradient(206.57deg, rgb(204, 204, 255) 16.67%, rgb(75, 75, 249) 100%);',
  },
)

const emit = defineEmits<{
  'update:gradient': [value: string]
  selected: []
}>()
const editableGradient = ref(props.gradient)

watch(editableGradient, (newValue) => {
  emit('update:gradient', newValue)
})

watch(
  () => props.gradient,
  (newValue) => {
    editableGradient.value = newValue
  },
)
</script>

<style lang="scss" scoped>
@use 'sass:color';

.UIGradientSphere {
  --sphere-size: 2.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  pointer-events: none;

  &--is-configurable {
    pointer-events: auto;
    cursor: pointer;
  }

  .UIGradientSphere__sphere {
    border-radius: 50%;
    cursor: pointer;
    width: var(--sphere-size);
    height: var(--sphere-size);

    &--sm {
      --sphere-size: 1rem;
    }

    &--md {
      --sphere-size: 2.25rem;
    }

    // Temporary for "extra" medium... Genius
    &--xmd {
      --sphere-size: 2.5rem;
    }

    &--lg {
      --sphere-size: 4rem;
    }
  }

  @keyframes hover {
    0% {
      transform: translate(0, 0px);
    }

    50% {
      transform: translate(0, -10px);
    }

    100% {
      transform: translate(0, 0px);
    }
  }

  @keyframes hover-shadow {
    0% {
      box-shadow: 0 100px 20px color.adjust($color-black, $lightness: 50%);
    }

    50% {
      box-shadow: 0 120px 40px color.adjust($color-black, $lightness: 50%);
    }

    100% {
      box-shadow: 0 100px 20px color.adjust($color-black, $lightness: 50%);
    }
  }

  .UIGradientSphere__sphere--floating {
    transform: translatey(0px);
    animation: hover 1.5s ease 0s infinite;
    &:after {
      content: '';
      position: absolute;
      left: 10%;
      top: 16%;
      z-index: -1;
      box-shadow: 0 100px 20px color.adjust($color-black, $lightness: 50%);
      border-radius: 50%;
      width: 50px;
      height: 50px;
      transform: rotateX(70deg);
      animation: hover-shadow 1.5s ease 0s infinite;
    }
  }
}
</style>
